import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { Monitor, Shield, Mail, Lock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMyDevicesFn } from "@/lib/device.functions";
import { currentDeviceId, parseDeviceInfo } from "@/lib/device-id";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/_authenticated/devices")({
  head: () => ({
    meta: [
      { title: "Your devices — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DevicesPage,
});

function DevicesPage() {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const fetchDevices = useServerFn(getMyDevicesFn);
  const { data, isLoading } = useQuery({
    queryKey: ["my-devices"],
    queryFn: () => fetchDevices(),
  });

  const myId = currentDeviceId();
  const devices = (data?.devices ?? []).filter((d) => d.is_active);
  const isFreelancer = profile?.role === "freelancer";
  const max = 3;
  const locale = i18n.language?.startsWith("ar") ? "ar" : "en";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("devices.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isFreelancer ? t("devices.subtitleFreelancer") : t("devices.subtitleOther")}
          </p>
        </div>
        {isFreelancer ? (
          <div className="inline-flex items-center gap-2 rounded-full border bg-muted/50 px-3 py-1.5 text-xs">
            <Lock className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium tabular-nums">
              {devices.length} / {max}
            </span>
            <span className="text-muted-foreground">{t("devices.locked")}</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs">
            <Globe className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="font-medium tabular-nums">{devices.length}</span>
            <span className="text-muted-foreground">{t("devices.activeUnlimited")}</span>
          </div>
        )}
      </div>

      <div className="rounded-2xl border bg-card p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            {t("devices.loading")}
          </div>
        ) : devices.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("devices.empty")}
          </div>
        ) : (
          <ul className="divide-y">
            {devices.map((d) => {
              const info = parseDeviceInfo(d.user_agent ?? "");
              const isThisDevice = d.device_id === myId;
              return (
                <li key={d.id} className="flex items-start gap-4 py-4 first:pt-2 last:pb-2">
                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Monitor className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="truncate text-sm font-medium">
                        {d.device_name ?? info.name}
                      </div>
                      {isThisDevice && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          {t("devices.thisDevice")}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{info.browser} · {info.os}</span>
                      {d.ip_address && <span>{t("devices.ipPrefix")} {d.ip_address}</span>}
                      {d.location && <span>{d.location}</span>}
                      <span>{t("devices.lastActive", { when: new Date(d.last_seen_at).toLocaleString(locale) })}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {isFreelancer && (
        <div className="rounded-2xl border border-primary/25 bg-primary/5 p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Shield className="h-4 w-4" />
            </div>
            <div className="space-y-3">
              <p className="text-sm leading-relaxed">
                {t("devices.securityBody")}
                <a href="mailto:contact@lumvio.co" className="font-medium text-primary hover:underline">
                  contact@lumvio.co
                </a>
                .
              </p>
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <a href="mailto:contact@lumvio.co?subject=Reset%20my%20devices">
                  <Mail className="me-2 h-3.5 w-3.5" /> {t("devices.contactSupport")}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
